import React, { useState, useMemo } from "react";
import { useAuth } from "@/lib/AuthContext";
import { entities, getDisplayMessage } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, ChevronLeft, ChevronRight, Plus, Clock, MapPin, User, Trash2, Edit, Phone, Mail, FileText, CalendarDays } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, differenceInMinutes } from "date-fns";
import { safeParseISO } from "@/utils";
import { ptBR } from "date-fns/locale";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EventoFormDialog from "../components/agenda/EventoFormDialog";
import VisaoSemana from "../components/agenda/VisaoSemana";
import VisaoEditais from "../components/agenda/VisaoEditais";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function AgendaPage() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [visaoAtiva, setVisaoAtiva] = useState("mes");
  const [filtroResponsavel, setFiltroResponsavel] = useState("todos");

  const queryClient = useQueryClient();

  const { user: currentUser } = useAuth();

  const { data: eventosRaw = [], isLoading } = useQuery({
    queryKey: ["agenda-eventos"],
    queryFn: () => entities.AgendaEvent.list("-data_inicio"),
  });

  const eventosRawSafe = Array.isArray(eventosRaw) ? eventosRaw : [];
  const eventos = useMemo(() => {
    if (filtroResponsavel === "todos") return eventosRawSafe;
    const email = currentUser?.email;
    if (filtroResponsavel === "meus") {
      return eventosRawSafe.filter(e =>
        e.created_by === email || (e.responsavelEmail ?? e.responsavel_email) === email
      );
    }
    return eventosRawSafe.filter(e => (e.responsavelEmail ?? e.responsavel_email) === filtroResponsavel);
  }, [eventosRawSafe, filtroResponsavel, currentUser]);

  const { data: users = [] } = useQuery({
    queryKey: ["users-agenda"],
    queryFn: () => entities.User.list(), // O tratamento de erro 403 já está no entities.User.list()
    retry: false, // Não tentar novamente
    onError: (error) => {
      // Não logar erros 403 ou silenciosos - eles já são tratados internamente
      if (error?.status !== 403 && !error?.isForbidden && !error?.silent) {
        console.error('Erro ao carregar usuários:', error);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => entities.AgendaEvent.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["agenda-eventos"]);
      toast.success("Evento excluído com sucesso");
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    },
    onError: (error) => {
      toast.error(getDisplayMessage(error, "Erro ao excluir evento. Tente novamente."), { duration: 5000 });
    },
  });

  const daysInMonth = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const firstDayOfMonth = startOfMonth(currentMonth).getDay();
  const emptyDays = Array(firstDayOfMonth).fill(null);

  const getDataInicio = (e) => e.dataInicio ?? e.data_inicio;

  const safeFormatTime = (dateStr) => {
    const d = dateStr && typeof dateStr === 'string' ? safeParseISO(dateStr) : null;
    return d ? format(d, "HH:mm", { locale: ptBR }) : '-';
  };
  const getEventosForDate = (date) => {
    return eventos.filter((evento) => {
      const d = getDataInicio(evento);
      if (!d || typeof d !== 'string') return false;
      const eventoDate = safeParseISO(d);
      return eventoDate ? isSameDay(eventoDate, date) : false;
    });
  };

  const selectedDateEventos = useMemo(() => {
    return getEventosForDate(selectedDate).sort((a, b) => {
      return new Date(getDataInicio(a)) - new Date(getDataInicio(b));
    });
  }, [selectedDate, eventos]);

  const hasEventosOnDate = (date) => {
    return eventos.some((evento) => {
      const d = getDataInicio(evento);
      if (!d || typeof d !== 'string') return false;
      const eventoDate = safeParseISO(d);
      return eventoDate ? isSameDay(eventoDate, date) : false;
    });
  };

  const getEventColorClass = (tipo) => {
    const colors = {
      "Retorno Pregão": "bg-red-500",
      "Visita Cliente": "bg-blue-500",
      "Reunião": "bg-purple-500",
      "Entrega Proposta": "bg-green-500",
      "Abertura Licitação": "bg-yellow-500",
      Outros: "bg-gray-500",
    };
    return colors[tipo] || colors.Outros;
  };

  const getStatusBadge = (status) => {
    const config = {
      Agendado: { label: "Agendado", className: "bg-blue-100 text-blue-800" },
      "Em Andamento": { label: "Em Andamento", className: "bg-yellow-100 text-yellow-800" },
      Concluído: { label: "Concluído", className: "bg-green-100 text-green-800" },
      Cancelado: { label: "Cancelado", className: "bg-red-100 text-red-800" },
    };
    return config[status] || config.Agendado;
  };

  const calcularDuracao = (dataInicio, dataFim) => {
    if (!dataInicio || !dataFim || typeof dataInicio !== 'string' || typeof dataFim !== 'string') return null;
    try {
      const dFim = safeParseISO(dataFim);
      const dInicio = safeParseISO(dataInicio);
      if (!dFim || !dInicio) return null;
      const diff = differenceInMinutes(dFim, dInicio);
      const horas = Math.floor(diff / 60);
      const minutos = diff % 60;
      if (horas === 0) return `${minutos}min`;
      if (minutos === 0) return `${horas}h`;
      return `${horas}h ${minutos}min`;
    } catch {
      return null;
    }
  };

  const handleEdit = (evento) => {
    setEditingEvent(evento);
    setDialogOpen(true);
  };

  const handleDelete = (evento) => {
    setEventToDelete(evento);
    setDeleteDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingEvent(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6" data-tour="tour-Agenda-root">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Agenda</h1>
            <p className="text-slate-600">Gerencie seus compromissos e eventos</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={filtroResponsavel} onValueChange={setFiltroResponsavel}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os eventos</SelectItem>
                <SelectItem value="meus">Meus eventos</SelectItem>
                {(Array.isArray(users) ? users : []).filter(u => u.company_id === currentUser?.company_id).map(user => (
                  <SelectItem key={user.id} value={user.email}>
                    {user.full_name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-tour="tour-Agenda-primary">
              <Plus className="w-4 h-4 mr-2" />
              Novo Evento
            </Button>
          </div>
        </div>

        {/* Tabs de Visão */}
        <Tabs value={visaoAtiva} onValueChange={setVisaoAtiva} className="mb-6">
          <TabsList className="grid w-full grid-cols-4 max-w-2xl">
            <TabsTrigger value="dia" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Dia
            </TabsTrigger>
            <TabsTrigger value="semana" className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Semana
            </TabsTrigger>
            <TabsTrigger value="mes" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Mês
            </TabsTrigger>
            <TabsTrigger value="editais" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Editais
            </TabsTrigger>
          </TabsList>

          {/* Visão Mês (padrão) */}
          <TabsContent value="mes" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendário */}
          <Card className="lg:col-span-2 shadow-lg">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h2 className="text-xl font-bold capitalize">
                  {format(currentMonth, "MMMM 'de' yyyy", { locale: ptBR })}
                </h2>
                <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {/* Dias da semana */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((dia) => (
                  <div key={dia} className="text-center text-sm font-semibold text-slate-600 py-2">
                    {dia}
                  </div>
                ))}
              </div>

              {/* Dias do mês */}
              <div className="grid grid-cols-7 gap-2">
                {emptyDays.map((_, index) => (
                  <div key={`empty-${index}`} className="aspect-square" />
                ))}
                {daysInMonth.map((day) => {
                  const hasEventos = hasEventosOnDate(day);
                  const isSelected = isSameDay(day, selectedDate);
                  const isToday = isSameDay(day, new Date());
                  const eventosCount = getEventosForDate(day).length;

                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => setSelectedDate(day)}
                      className={`aspect-square rounded-xl p-2 relative transition-all hover:shadow-md ${
                        isSelected
                          ? "bg-blue-600 text-white shadow-lg"
                          : isToday
                          ? "bg-blue-100 text-blue-900 font-semibold"
                          : "bg-white hover:bg-slate-50"
                      }`}
                    >
                      <span className="text-sm">{format(day, "d")}</span>
                      {hasEventos && (
                        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-1">
                          <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : "bg-blue-600"}`} />
                        </div>
                      )}
                      {eventosCount > 1 && (
                        <div
                          className={`absolute top-1 right-1 text-xs font-bold ${
                            isSelected ? "text-white" : "text-blue-600"
                          }`}
                        >
                          {eventosCount}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Lista de Eventos do Dia */}
          <Card className="shadow-lg max-h-[600px] overflow-y-auto">
            <CardHeader className="border-b sticky top-0 bg-white z-10">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                {format(selectedDate, "dd 'de' MMMM", { locale: ptBR })}
              </CardTitle>
              <p className="text-sm text-slate-600">{selectedDateEventos.length} evento(s)</p>
            </CardHeader>
            <CardContent className="p-3 space-y-3">
              {selectedDateEventos.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum evento neste dia</p>
                  <Button
                    variant="link"
                    className="text-blue-600 mt-2"
                    onClick={() => setDialogOpen(true)}
                  >
                    Criar evento
                  </Button>
                </div>
              ) : (
                selectedDateEventos.map((evento) => {
                  const statusBadge = getStatusBadge(evento.status);
                  const duracao = calcularDuracao(getDataInicio(evento), evento.dataFim ?? evento.data_fim);
                  const responsavel = users.find((u) => u.email === (evento.responsavelEmail ?? evento.responsavel_email));

                  return (
                    <Card key={evento.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        {/* Cabeçalho do evento */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`w-1 h-full rounded-full ${getEventColorClass(evento.tipo_evento)}`} />
                            <div className="flex-1">
                              <h3 className="font-semibold text-slate-900 mb-1">{evento.titulo}</h3>
                              <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                                <Clock className="w-3.5 h-3.5" />
                                <span>
                                  {safeFormatTime(getDataInicio(evento))}
                                  {(evento.dataFim ?? evento.data_fim) && ` - ${safeFormatTime(evento.dataFim ?? evento.data_fim)}`}
                                </span>
                                {duracao && (
                                  <>
                                    <span className="text-slate-400">•</span>
                                    <span>{duracao}</span>
                                  </>
                                )}
                              </div>
                              <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(evento)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(evento)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Responsável */}
                        {(evento.responsavelEmail ?? evento.responsavel_email) && (
                          <div className="flex items-center gap-2 mb-2 p-2 bg-slate-50 rounded-lg">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {(responsavel?.full_name) || (evento.responsavelNome ?? evento.responsavel_nome) || (evento.responsavelEmail ?? evento.responsavel_email)}
                              </p>
                              <p className="text-xs text-slate-600">Responsável</p>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => (window.location.href = `mailto:${(evento.responsavelEmail ?? evento.responsavel_email)}`)}
                              >
                                <Mail className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Descrição */}
                        {evento.descricao && (
                          <p className="text-sm text-slate-600 mb-2 line-clamp-2">{evento.descricao}</p>
                        )}

                        {/* Local */}
                        {evento.local && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>{evento.local}</span>
                          </div>
                        )}

                        {/* Tipo de evento */}
                        <div className="mt-2">
                          <Badge variant="outline" className="text-xs">
                            {evento.tipo_evento}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>
          </TabsContent>

          {/* Visão Dia */}
          <TabsContent value="dia" className="mt-6">
            <Card className="shadow-lg">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="icon" onClick={() => {
                    const newDate = new Date(selectedDate);
                    newDate.setDate(newDate.getDate() - 1);
                    setSelectedDate(newDate);
                  }}>
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <CardTitle className="text-xl">
                    {format(selectedDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => {
                    const newDate = new Date(selectedDate);
                    newDate.setDate(newDate.getDate() + 1);
                    setSelectedDate(newDate);
                  }}>
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {selectedDateEventos.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-semibold">Nenhum evento neste dia</p>
                    <Button variant="link" className="text-blue-600 mt-2" onClick={() => setDialogOpen(true)}>
                      Criar evento
                    </Button>
                  </div>
                ) : (
                  selectedDateEventos.map((evento) => {
                    const statusBadge = getStatusBadge(evento.status);
                    const duracao = calcularDuracao(getDataInicio(evento), evento.dataFim ?? evento.data_fim);
                    const responsavel = users.find((u) => u.email === (evento.responsavelEmail ?? evento.responsavel_email));

                    return (
                      <Card key={evento.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start gap-3 flex-1">
                              <div className={`w-1.5 h-full rounded-full ${getEventColorClass(evento.tipo_evento)}`} />
                              <div className="flex-1">
                                <h3 className="font-semibold text-slate-900 text-lg mb-2">{evento.titulo}</h3>
                                <div className="flex items-center gap-3 text-sm text-slate-600 mb-3">
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    <span>
                                      {safeFormatTime(getDataInicio(evento))}
                                      {(evento.dataFim ?? evento.data_fim) && ` - ${safeFormatTime(evento.dataFim ?? evento.data_fim)}`}
                                    </span>
                                  </div>
                                  {duracao && (
                                    <>
                                      <span className="text-slate-400">•</span>
                                      <span>{duracao}</span>
                                    </>
                                  )}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                  <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
                                  <Badge variant="outline">{evento.tipo_evento}</Badge>
                                  {evento.compartilhar_com_equipe && (
                                    <Badge className="bg-purple-100 text-purple-800">Compartilhado</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(evento)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete(evento)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {(evento.responsavelEmail ?? evento.responsavel_email) && (
                            <div className="flex items-center gap-2 mb-3 p-3 bg-slate-50 rounded-lg">
                              <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-5 h-5 text-blue-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">
                                  {(responsavel?.full_name) || (evento.responsavelNome ?? evento.responsavel_nome) || (evento.responsavelEmail ?? evento.responsavel_email)}
                                </p>
                                <p className="text-xs text-slate-600">Responsável</p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => (window.location.href = `mailto:${(evento.responsavelEmail ?? evento.responsavel_email)}`)}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            </div>
                          )}

                          {evento.descricao && <p className="text-sm text-slate-600 mb-3">{evento.descricao}</p>}

                          {evento.local && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <MapPin className="w-4 h-4" />
                              <span>{evento.local}</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Visão Semana */}
          <TabsContent value="semana" className="mt-6">
            <VisaoSemana
              selectedDate={selectedDate}
              eventos={eventos}
              onEdit={handleEdit}
              onDelete={handleDelete}
              getEventColorClass={getEventColorClass}
              getStatusBadge={getStatusBadge}
            />
          </TabsContent>

          {/* Visão Editais */}
          <TabsContent value="editais" className="mt-6">
            <VisaoEditais
              eventos={eventos}
              onEdit={handleEdit}
              onDelete={handleDelete}
              getEventColorClass={getEventColorClass}
              getStatusBadge={getStatusBadge}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog de criação/edição */}
      <EventoFormDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        evento={editingEvent}
        selectedDate={selectedDate}
      />

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o evento "{eventToDelete?.titulo}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(eventToDelete.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}